// Einstellungen → Eigene Kundenfelder: Mandant definiert frei eigene
// Kundenfelder (Auswahlliste, Text, Zahl, Datum, Ja/Nein …), legt Typ,
// Optionen, Sichtbarkeit, Pflicht & Reihenfolge fest.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronUp, ChevronDown, Save, ListChecks, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'select', label: 'Auswahlliste' },
  { value: 'multiselect', label: 'Mehrfachauswahl' },
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textbereich' },
  { value: 'number', label: 'Zahl' },
  { value: 'date', label: 'Datum' },
  { value: 'boolean', label: 'Ja / Nein' },
];
const VIS_KEYS = [
  { key: 'profile', label: 'Kundenprofil' },
  { key: 'appointment_menu', label: 'Termin-Menü' },
  { key: 'appointment_block', label: 'Termin-Block' },
  { key: 'day_list', label: 'Tagesliste' },
  { key: 'customer_app', label: 'Kunden-App' },
];
const hasOptions = (t) => t === 'select' || t === 'multiselect';

const CustomFieldsSettingsPage = ({ embedded = false }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState('text');
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/settings/custom-fields`);
      setFields(res.data?.fields || []);
    } catch {
      toast.error('Felder konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patchLocal = (id, patch) => setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const createField = async () => {
    if (!newLabel.trim()) { toast.error('Bezeichnung fehlt'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/settings/custom-fields`, {
        label: newLabel.trim(), type: newType,
        visibility: { profile: true },
      });
      setNewLabel(''); setNewType('text');
      toast.success('Feld angelegt');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  const saveField = async (f) => {
    setSavingId(f.id);
    try {
      await axios.put(`${API}/settings/custom-fields/${f.id}`, {
        label: f.label, type: f.type,
        options: hasOptions(f.type) ? (f.options || []) : [],
        unit: f.unit || '',
        visibility: f.visibility || {},
        required_booking: !!f.required_booking,
        enabled: !!f.enabled,
      });
      toast.success('Gespeichert');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSavingId(null);
    }
  };

  const deleteField = async (f) => {
    if (!window.confirm(`Feld "${f.label}" löschen? Vorhandene Werte bei Kunden bleiben erhalten, werden aber nicht mehr angezeigt.`)) return;
    try {
      await axios.delete(`${API}/settings/custom-fields/${f.id}`);
      toast.success('Gelöscht');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const move = async (index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= fields.length) return;
    const next = [...fields];
    const [item] = next.splice(index, 1);
    next.splice(to, 0, item);
    setFields(next);
    try {
      await axios.put(`${API}/settings/custom-fields/reorder`, { order: next.map((f) => f.id) });
    } catch {
      toast.error('Reihenfolge konnte nicht gespeichert werden');
      load();
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>;
  }

  return (
    <div className="space-y-5" data-testid="custom-fields-settings">
      {!embedded && (
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600"><ListChecks className="w-5 h-5" /></div>
          <div>
            <h2 className="text-lg font-semibold">Eigene Kundenfelder</h2>
            <p className="text-sm text-muted-foreground">Definiere eigene Felder für deine Kunden (z.&nbsp;B. EMS-Anzugsgrösse, Schuhgrösse, Hinweise) und lege fest, wo sie sichtbar sind.</p>
          </div>
        </div>
      )}

      {/* Neues Feld */}
      <Card data-testid="custom-field-create">
        <CardContent className="pt-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs">Bezeichnung</Label>
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="z.B. Schuhgrösse" data-testid="custom-field-new-label" />
          </div>
          <div className="w-44">
            <Label className="text-xs">Feldtyp</Label>
            <Select value={newType} onValueChange={setNewType}>
              <SelectTrigger data-testid="custom-field-new-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={createField} disabled={creating} data-testid="custom-field-add-btn">
            <Plus className="w-4 h-4 mr-2" /> Feld hinzufügen
          </Button>
        </CardContent>
      </Card>

      {!fields.length ? (
        <div className="p-8 text-center text-muted-foreground" data-testid="custom-fields-settings-empty">Noch keine Felder definiert.</div>
      ) : (
        <div className="space-y-3">
          {fields.map((f, idx) => (
            <Card key={f.id} data-testid={`custom-field-card-${f.key}`}>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-muted-foreground disabled:opacity-30" data-testid={`custom-field-up-${f.key}`}><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => move(idx, 1)} disabled={idx === fields.length - 1} className="text-muted-foreground disabled:opacity-30" data-testid={`custom-field-down-${f.key}`}><ChevronDown className="w-4 h-4" /></button>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Bezeichnung</Label>
                    <Input value={f.label} onChange={(e) => patchLocal(f.id, { label: e.target.value })} data-testid={`custom-field-label-${f.key}`} />
                  </div>
                  <div className="w-44">
                    <Label className="text-xs">Feldtyp</Label>
                    <Select value={f.type} onValueChange={(v) => patchLocal(f.id, { type: v })}>
                      <SelectTrigger data-testid={`custom-field-type-${f.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 text-sm whitespace-nowrap">
                    <Switch checked={!!f.enabled} onCheckedChange={(c) => patchLocal(f.id, { enabled: c })} data-testid={`custom-field-enabled-${f.key}`} /> Aktiv
                  </label>
                </div>

                {hasOptions(f.type) && (
                  <div>
                    <Label className="text-xs">Optionen (mit Komma trennen)</Label>
                    <Input
                      value={f._optionsText ?? (f.options || []).join(', ')}
                      onChange={(e) => patchLocal(f.id, { _optionsText: e.target.value, options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                      placeholder="z.B. 1, 2, 2.5, 3, 4, 4NEU"
                      data-testid={`custom-field-options-${f.key}`}
                    />
                  </div>
                )}
                {(f.type === 'number' || f.type === 'text') && (
                  <div className="w-40">
                    <Label className="text-xs">Einheit (optional)</Label>
                    <Input value={f.unit || ''} onChange={(e) => patchLocal(f.id, { unit: e.target.value })} placeholder="z.B. cm" data-testid={`custom-field-unit-${f.key}`} />
                  </div>
                )}

                <div>
                  <Label className="text-xs">Sichtbar in</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {VIS_KEYS.map((vk) => {
                      const active = !!(f.visibility || {})[vk.key];
                      return (
                        <button key={vk.key} type="button"
                          onClick={() => patchLocal(f.id, { visibility: { ...(f.visibility || {}), [vk.key]: !active } })}
                          className={`px-3 py-1 rounded-full border text-sm transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
                          data-testid={`custom-field-vis-${f.key}-${vk.key}`}>
                          {vk.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={!!f.required_booking} onCheckedChange={(c) => patchLocal(f.id, { required_booking: c })} data-testid={`custom-field-required-${f.key}`} /> Pflichtfeld bei Buchung
                  </label>
                  <div className="flex gap-2">
                    <Button variant="outline" className="text-red-600" onClick={() => deleteField(f)} data-testid={`custom-field-delete-${f.key}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => saveField(f)} disabled={savingId === f.id} data-testid={`custom-field-save-${f.key}`}>
                      {savingId === f.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Speichern
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomFieldsSettingsPage;
